/**
 * @file warehouse-change-confirmation.tsx
 * @module app/modals/warehouse-change-confirmation
 * @description Renders the warehouse change confirmation modal dialog for confirming actions, capturing workflow input, and reporting outcomes to its parent screen.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import Modal from 'react-bootstrap/Modal';
import { Button } from 'react-bootstrap';
import CrossSvg from '../../_admin/assets/media/close.png';

interface WarehouseChangeConfirmationProps {
  show: boolean;
  onHide: () => void;
  fromWarehouse?: string | null;
  toWarehouse?: string | null;
  onConfirm: () => void;
  loading?: boolean;
}

const WarehouseChangeConfirmation = (
  props: WarehouseChangeConfirmationProps
) => {
  const {
    show,
    onHide,
    fromWarehouse,
    toWarehouse,
    onConfirm,
    loading = false,
  } = props;

  const titleId = 'warehouse-change-title';
   const descriptionId = 'warehouse-change-description';
   const targetLabel = toWarehouse || 'this warehouse';

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="modal-dialog-centered modal-dialog-warehouse-change"
      className="border-r10px"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      role="dialog"
      centered
      animation
    >
      <Modal.Header className="border-bottom-0 pb-0">
        <div className="d-flex w-100 justify-content-between align-items-start">
          <Modal.Title
            id={titleId}
            className="fs-26 fw-bolder text-start"
          >
            Change Warehouse
          </Modal.Title>
          <img
            className="cursor-pointer"
            width={40}
            height={40}
            src={CrossSvg}
            alt="close button"
            onClick={onHide}
          />
        </div>
      </Modal.Header>
      <Modal.Body className="pt-3 pb-2">
        <div
          className="mx-auto text-start"
          style={{ maxWidth: 500 }}
          id={descriptionId}
        >
          <div className="mb-4 bg-light-primary border-r10px p-4 d-flex align-items-center justify-content-between gap-4">
            <div>
              <div className="fw-bolder fs-18">
                {fromWarehouse || 'N/A'}
              </div>
            </div>
            <div className="d-flex flex-column align-items-center justify-content-center flex-shrink-0">
              <span className="fs-24">→</span>
            </div>
            <div>
              <div className="fw-bolder fs-18">
                {toWarehouse || 'N/A'}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <div className="fw-500 mb-2">What will happen:</div>
            <div className="bg-light border border-r10px p-3 fs-14">
              This will update all data and views to the selected warehouse.
              Any unsaved changes in the current warehouse will be discarded
              and cannot be undone.
            </div>
          </div>

          <div className="mb-2">
            <div className="bg-light-danger border-r10px p-3 d-flex align-items-start gap-2">
              <span className="fs-18 lh-1">⚠</span>
              <span className="fs-14">
                Unsaved changes cannot be recovered after switching.
              </span>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0 pb-4">
        <div className="d-flex justify-content-end w-100 gap-3">
          <Button className="uw-btn uw-btn-danger uw-btn-lg"
            variant="danger"
            size="lg"
            onClick={onHide}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button className="uw-btn uw-btn-primary uw-btn-lg"
            variant="primary"
            size="lg"
            onClick={onConfirm}
            disabled={loading}
          >
            {!loading && (
              <span className="indicator-label">
                Switch to {targetLabel}
              </span>
            )}
            {loading && (
              <span
                className="indicator-progress"
                style={{ display: 'block' }}
              >
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default WarehouseChangeConfirmation;
